import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { X, Plus, Loader2, Check, ExternalLink, CreditCard, Banknote, Radio, Truck, Monitor, Wallet } from "lucide-react";

const GATEWAY_INFO = {
  stripe: {
    name: "Stripe",
    color: "bg-[#635BFF]",
    textColor: "text-[#635BFF]",
    borderColor: "border-[#635BFF]",
    signupUrl: "https://dashboard.stripe.com/login_success?redirect=%2Facct_1T5TNsPV7wAuvNRy",
    signupLabel: "Crear cuenta Stripe",
    fields: [
      { key: "secret_key", label: "Secret Key", placeholder: "sk_live_...", type: "password" },
      { key: "publishable_key", label: "Publishable Key", placeholder: "pk_live_...", type: "text" },
    ],
  },
  viva: {
    name: "Viva Wallet",
    color: "bg-[#00B4D8]",
    textColor: "text-[#00B4D8]",
    borderColor: "border-[#00B4D8]",
    signupUrl: "https://accounts.vivapayments.com/Account/Login?ReturnUrl=%2Fconnect%2Fauthorize%2Fcallback%3Fclient_id%3Dselfcare.apps.vivapayments.com%26redirect_uri%3Dhttps%253A%252F%252Fwww.vivapayments.com%252F%26response_type%3Dcode%2520id_token%26scope%3Dopenid%2520roles%26state%3DOpenIdConnect.AuthenticationProperties%253DIxKp9l6oXNon2h4WadeZnKI8j3UmmT_gy2kz29A_eo6nCUlO1Zr1TaEW15BAGBYuxJxOOaj6Uvp1TOXI30Sc4EG58te1AxE45grGrwbcM2bMihI1O7fB2GQVKrEHnlcbO0UOrd5gGn2O0IV8OEARgB2NnBxhOHrhOjbwU_telPJexFPOm9VDAxIcxhq_iB9V4_9i2v5JMSW-VQuD_5NW40p22Y_1LmJvl5EiZNqUT3k%26response_mode%3Dform_post%26ui_locales%3Den%26nonce%3D639080498634590935.NTk5OGU0YWQtYzUxYS00ZDAyLWJmM2YtNTEzYmJiNzY1NzU4MmVmNzgzMDYtYmQ4MS00MDVlLWIyZTUtMzZkMmFjODFkMGUz%26x-client-SKU%3DID_NET461%26x-client-ver%3D5.3.0.0",
    signupLabel: "Acceder a Viva Wallet",
    fields: [
      { key: "client_id", label: "Smart Checkout Client ID", placeholder: "xxxxx.apps.vivapayments.com", type: "text" },
      { key: "client_secret", label: "Smart Checkout Client Secret", placeholder: "Client Secret", type: "password" },
      { key: "website_code", label: "Codigo do Website/App", placeholder: "7099", type: "text" },
      { key: "merchant_id", label: "ID do Comerciante", placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx", type: "text" },
      { key: "api_key", label: "Chave API", placeholder: "API Key", type: "password" },
    ],
  },
  revolut: {
    name: "Revolut Business",
    color: "bg-[#0075EB]",
    textColor: "text-[#0075EB]",
    borderColor: "border-[#0075EB]",
    signupUrl: "https://business.revolut.com/signup?promo=B2B-FEB1-26-AR-H2&ext=order-checkout-flow&context=B2B_REFERRAL",
    signupLabel: "Crear cuenta Revolut Business",
    signupNote: "Primer mes sin tasas de suscripcion!",
    fields: [
      { key: "api_key", label: "API Key", placeholder: "sk_live_...", type: "password" },
      { key: "merchant_id", label: "Merchant ID", placeholder: "merchant_...", type: "text" },
    ],
  },
  redsys: {
    name: "Redsys TPV Virtual",
    color: "bg-[#C8102E]",
    textColor: "text-[#C8102E]",
    borderColor: "border-[#C8102E]",
    signupUrl: "https://www.redsys.es/en/",
    signupLabel: "Web Redsys",
    signupNote: "Integración con los bancos españoles (Santander, CaixaBank, BBVA…)",
    fields: [
      { key: "merchant_code", label: "Código de Comerciante (FUC)", placeholder: "999008881", type: "text" },
      { key: "terminal", label: "Número de Terminal", placeholder: "1", type: "text" },
      { key: "secret_key", label: "Clave SHA-256 (en Base64)", placeholder: "sq7HjrUOBfKmC576ILgskD5srU870gJ7", type: "password" },
    ],
    extraToggle: { key: "sandbox", label: "Modo Sandbox (pruebas)", defaultValue: true },
  },
};

export const PagosTab = ({ authAxios, restaurant, onUpdate }) => {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [kaisopayEnabled, setKaisopayEnabled] = useState(false);

  // Check if KaisoPay is enabled
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    authAxiosRef.current.get("/kaisopay/config")
      .then(r => setKaisopayEnabled(r.data?.enabled || false))
      .catch(() => {});
  }, []);

  // Sync from restaurant prop — if KaisoPay was disabled from another tab, reflect it here
  useEffect(() => {
    if (restaurant?.kaisopay_enabled === false) setKaisopayEnabled(false);
    else if (restaurant?.kaisopay_enabled === true) setKaisopayEnabled(true);
  }, [restaurant?.kaisopay_enabled]);
  
  const DEFAULT_METHODS = [
    { name: "Efectivo", fee: 0, pct: 0, active: true, delivery: true },
    { name: "Visa (Debito)", fee: 0, pct: 1.5, active: true, delivery: true },
    { name: "Mastercard (Credito)", fee: 0, pct: 2.0, active: true, delivery: true },
    { name: "Bizum", fee: 0, pct: 0, active: false, delivery: true },
    { name: "MBWay", fee: 0, pct: 0, active: false, delivery: true },
  ];
  // Normalize payment_methods: convert old string format to objects
  const normalizedMethods = (() => {
    const raw = restaurant?.payment_methods;
    if (!raw || !Array.isArray(raw) || raw.length === 0) return DEFAULT_METHODS;
    if (typeof raw[0] === "string") return DEFAULT_METHODS; // Old format, use defaults
    return raw;
  })();
  const [methods, setMethods] = useState(normalizedMethods);
  const [saving, setSaving] = useState(false);

  // Gateway selection - only one active at a time
  const [activeGateway, setActiveGateway] = useState(restaurant?.active_payment_gateway || "");

  // Viva Wallet config
  const vivaInit = restaurant?.viva_wallet || {};
  const [vivaConfig, setVivaConfig] = useState({
    client_id: vivaInit.client_id || "",
    client_secret: vivaInit.client_secret || "",
    merchant_id: vivaInit.merchant_id || "",
    api_key: vivaInit.api_key || "",
    website_code: vivaInit.website_code || "",
    demo_mode: vivaInit.demo_mode || false,
  });

  // Stripe config (per-restaurant)
  const stripeInit = restaurant?.stripe_config || {};
  const [stripeConfig, setStripeConfig] = useState({
    secret_key: stripeInit.secret_key || "",
    publishable_key: stripeInit.publishable_key || "",
  });

  // Revolut config
  const revolutInit = restaurant?.revolut_config || {};
  const [revolutConfig, setRevolutConfig] = useState({
    api_key: revolutInit.api_key || "",
    merchant_id: revolutInit.merchant_id || "",
  });

  // Redsys config
  const redsysInit = restaurant?.redsys_config || {};
  const [redsysConfig, setRedsysConfig] = useState({
    merchant_code: redsysInit.merchant_code || "",
    terminal: redsysInit.terminal || "1",
    secret_key: redsysInit.secret_key || "",
    sandbox: redsysInit.sandbox !== false,
  });

  // Bizum phone
  const [bizumPhone, setBizumPhone] = useState(restaurant?.reservation_settings?.bizum_phone || "");

  // POS / Delivery operational toggles + extra payment
  const [deliveryEnabled, setDeliveryEnabled] = useState(restaurant?.delivery_enabled !== false);
  const [posEnabled, setPosEnabled] = useState(restaurant?.pos_enabled !== false);
  const [extraPaymentMethod, setExtraPaymentMethod] = useState(restaurant?.extra_payment_method || "");

  const addMethod = () => setMethods(prev => [...prev, { name: "", fee: 0, pct: 0, active: true, delivery: true, pos_only: false }]);
  const updateMethod = (idx, field, value) => setMethods(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
  const removeMethod = (idx) => setMethods(prev => prev.filter((_, i) => i !== idx));

  const handleSelectGateway = async (gw) => {
    // Always re-fetch fresh KaisoPay status to avoid stale state when switching tabs
    let currentlyEnabled = kaisopayEnabled;
    try {
      const r = await authAxios.get("/kaisopay/config");
      currentlyEnabled = r.data?.enabled || false;
      setKaisopayEnabled(currentlyEnabled);
    } catch { /* use cached state */ }
    if (currentlyEnabled) {
      toast.error("Desative o KaisoPay primeiro para usar outro gateway");
      return;
    }
    setActiveGateway(prev => prev === gw ? "" : gw);
  };

  const getGatewayConfig = (gw) => {
    if (gw === "stripe") return stripeConfig;
    if (gw === "viva") return vivaConfig;
    if (gw === "revolut") return revolutConfig;
    if (gw === "redsys") return redsysConfig;
    return {};
  };

  const updateGatewayField = (gw, field, value) => {
    if (gw === "stripe") setStripeConfig(prev => ({ ...prev, [field]: value }));
    else if (gw === "viva") setVivaConfig(prev => ({ ...prev, [field]: value }));
    else if (gw === "revolut") setRevolutConfig(prev => ({ ...prev, [field]: value }));
    else if (gw === "redsys") setRedsysConfig(prev => ({ ...prev, [field]: value }));
  };

  const saveMethods = async () => {
    setSaving(true);
    try {
      await authAxios.patch("/restaurant", {
        payment_methods: methods,
        active_payment_gateway: activeGateway,
        viva_wallet: { ...vivaConfig, enabled: activeGateway === "viva" },
        stripe_config: stripeConfig,
        revolut_config: revolutConfig,
        redsys_config: { ...redsysConfig, enabled: activeGateway === "redsys" },
        reservation_settings: { ...(restaurant?.reservation_settings || {}), bizum_phone: bizumPhone },
        delivery_enabled: deliveryEnabled,
        pos_enabled: posEnabled,
        extra_payment_method: extraPaymentMethod.trim() || null,
      });
      toast.success("Configuracion de pagos guardada");
      onUpdate();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401) toast.error("Sessão expirada — faça login novamente");
      else if (status === 422 && detail) toast.error(`Erro de validação: ${detail}`);
      else if (detail) toast.error(`Erro: ${detail}`);
      else toast.error("Error al guardar");
      console.error("[PagosTab] save error:", status, err?.response?.data || err?.message);
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      {/* PAGO CONTRA ENTREGA */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Banknote className="w-5 h-5 text-emerald-600" />
            <CardTitle className="uppercase">Pago contra entrega</CardTitle>
          </div>
          <CardDescription>Medios de pago disponibles para clientes que pagan en la entrega.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {methods.map((method, idx) => (
            <div key={idx} className="border border-border/50 rounded-lg p-4 space-y-3" data-testid={`payment-method-${idx}`}>
              <div className="grid grid-cols-4 gap-4 items-end">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Metodo de pago</Label>
                  <Input value={method.name} onChange={e => updateMethod(idx, "name", e.target.value)} className="bg-secondary border-transparent" placeholder="Ej: Visa, Mastercard, Efectivo" data-testid={`method-name-${idx}`} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Tasa fija</Label>
                  <div className="flex items-center gap-1"><span className="text-muted-foreground">EUR</span><Input type="number" step="0.1" value={method.fee} onChange={e => updateMethod(idx, "fee", Number(e.target.value))} className="bg-secondary border-transparent" /></div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Tasa porcentual</Label>
                  <div className="flex items-center gap-1"><Input type="number" step="0.1" value={method.pct} onChange={e => updateMethod(idx, "pct", Number(e.target.value))} className="bg-secondary border-transparent" /><span className="text-muted-foreground">%</span></div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeMethod(idx)} data-testid={`remove-method-${idx}`}><X className="w-4 h-4 text-red-400" /></Button>
              </div>
              <div className="flex items-center gap-6 flex-wrap">
                <div className="flex items-center gap-2"><Switch checked={method.delivery && !method.pos_only} onCheckedChange={v => updateMethod(idx, "delivery", v)} disabled={method.pos_only} /><span className={`text-sm ${method.pos_only ? "text-muted-foreground" : ""}`}>Habilitar delivery</span></div>
                <div className="flex items-center gap-2"><Switch checked={method.active} onCheckedChange={v => updateMethod(idx, "active", v)} /><span className="text-sm">Activo</span></div>
                <div className="flex items-center gap-2">
                  <Switch checked={!!method.pos_only} onCheckedChange={v => { updateMethod(idx, "pos_only", v); if (v) updateMethod(idx, "delivery", false); }} />
                  <span className="text-sm font-medium text-orange-600">Só TPV</span>
                </div>
              </div>
            </div>
          ))}
          <Button variant="outline" className="text-primary border-primary" onClick={addMethod} data-testid="add-payment-method-btn">
            <Plus className="w-4 h-4 mr-2" />ADICIONAR METODO DE PAGO
          </Button>
        </CardContent>
      </Card>

      {/* BIZUM CONFIG */}
      {methods.some(m => m.name?.toLowerCase().includes("bizum") && m.active) && (
        <Card className="bg-card border-[#05C3DD]/30">
          <CardHeader>
            <div className="flex items-center gap-2">
              <svg viewBox="0 0 24 24" className="w-5 h-5"><rect width="24" height="24" rx="4" fill="#05C3DD"/><text x="12" y="16" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="800" fontFamily="Arial,sans-serif">Biz</text></svg>
              <CardTitle className="uppercase text-[#05C3DD]">Configuración Bizum</CardTitle>
            </div>
            <CardDescription>Configura el número de teléfono para recibir pagos Bizum.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Número Bizum del restaurante</Label>
              <Input
                value={bizumPhone}
                onChange={e => setBizumPhone(e.target.value)}
                placeholder="+34 600 000 000"
                className="bg-secondary border-transparent max-w-xs"
                data-testid="bizum-phone-input"
              />
              <p className="text-xs text-muted-foreground">El cliente verá este número para enviar el pago por Bizum.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* PAGO ONLINE - GATEWAY SELECTION */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-600" />
            <CardTitle className="uppercase">Pago Online</CardTitle>
          </div>
          <CardDescription>Seleccione un proveedor de pagos online. Solo un proveedor puede estar activo a la vez.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {kaisopayEnabled && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800 flex items-center justify-between gap-3">
              <span><span className="font-bold">KaisoPay ativo!</span> Os pagamentos online serao processados via KaisoPay. Para usar outro gateway, desative o KaisoPay primeiro.</span>
              <button
                className="shrink-0 text-xs font-semibold text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition"
                onClick={async () => {
                  try {
                    await authAxios.put("/kaisopay/config", { enabled: false });
                    setKaisopayEnabled(false);
                  } catch { toast.error("Erro ao desativar KaisoPay"); }
                }}
              >
                Desativar
              </button>
            </div>
          )}
          {["stripe", "viva", "revolut", "redsys"].map(gw => {
            const info = GATEWAY_INFO[gw];
            const isActive = activeGateway === gw;
            const config = getGatewayConfig(gw);

            return (
              <div
                key={gw}
                className={`border-2 rounded-xl overflow-hidden transition-all ${isActive ? `${info.borderColor} shadow-md` : 'border-border/50 opacity-75 hover:opacity-100'}`}
                data-testid={`gateway-${gw}`}
              >
                {/* Gateway Header */}
                <div
                  className={`flex items-center justify-between p-4 cursor-pointer ${isActive ? 'bg-white' : 'bg-secondary/30'}`}
                  onClick={() => handleSelectGateway(gw)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${isActive ? info.color : 'bg-gray-300'}`} />
                    <span className={`font-bold text-lg ${isActive ? info.textColor : 'text-gray-500'}`}>{info.name}</span>
                    {isActive && (
                      <span className="text-xs font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">ACTIVO</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={info.signupUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition hover:shadow-sm ${isActive ? `${info.textColor} ${info.borderColor}` : 'text-gray-500 border-gray-300'}`}
                      onClick={e => e.stopPropagation()}
                      data-testid={`gateway-${gw}-signup`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      {info.signupLabel}
                    </a>
                    <Switch
                      checked={isActive}
                      onCheckedChange={() => handleSelectGateway(gw)}
                      data-testid={`gateway-${gw}-toggle`}
                    />
                  </div>
                </div>

                {/* Gateway Config - Only show when active */}
                {isActive && (
                  <div className="border-t p-4 space-y-4 bg-white">
                    {info.signupNote && (
                      <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded-lg font-medium">{info.signupNote}</p>
                    )}

                    {gw === "viva" && (
                      <div className="flex items-center gap-3 pb-2">
                        <Switch
                          checked={vivaConfig.demo_mode}
                          onCheckedChange={v => setVivaConfig(prev => ({ ...prev, demo_mode: v }))}
                        />
                        <span className="text-sm text-muted-foreground">Modo Demo (sandbox de pruebas)</span>
                      </div>
                    )}

                    {gw === "redsys" && (
                      <div className="flex items-center gap-3 pb-2">
                        <Switch
                          checked={redsysConfig.sandbox}
                          onCheckedChange={v => setRedsysConfig(prev => ({ ...prev, sandbox: v }))}
                        />
                        <span className="text-sm text-muted-foreground">Modo Sandbox (pruebas — desactiva para producción)</span>
                      </div>
                    )}

                    <div className="grid gap-4">
                      {info.fields.map(field => (
                        <div key={field.key} className="space-y-1.5">
                          <Label className="text-sm font-medium">{field.label}</Label>
                          <Input
                            type={field.type}
                            value={config[field.key] || ""}
                            onChange={e => updateGatewayField(gw, field.key, e.target.value)}
                            placeholder={field.placeholder}
                            className="bg-secondary border-transparent font-mono text-sm"
                            data-testid={`${gw}-${field.key}`}
                          />
                        </div>
                      ))}
                    </div>

                    {gw === "redsys" && (
                      <>
                        <Separator />
                        <div className="bg-red-50 p-4 rounded-lg space-y-3">
                          <p className="text-sm font-semibold text-red-800">Configuración Redsys TPV Virtual</p>
                          <div className="space-y-3 text-xs text-red-700">
                            <p>1. Contrata el TPV Virtual con tu banco español (Santander, CaixaBank, BBVA, Sabadell…)</p>
                            <p>2. Accede al panel <strong>Redsys CANALES</strong> que te proporcionará tu banco</p>
                            <p>3. En <strong>Consulta de datos → Clave SHA-256</strong> encontrarás la clave secreta en Base64</p>
                            <p className="font-bold">URL de notificación (configúrala en el panel Redsys):</p>
                            <code className="text-xs bg-red-100 p-2 rounded block break-all select-all font-bold">{`https://api.kaisosystem.com/api/public/redsys/notify`}</code>
                            <p className="mt-2">Para pruebas usa el <strong>Modo Sandbox</strong> con tarjeta <code>4548812049400004</code> (CVV: 123)</p>
                          </div>
                        </div>
                      </>
                    )}

                    {gw === "viva" && (
                      <>
                        <Separator />
                        <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                          <p className="text-sm font-semibold text-blue-800">Configuracion en Dashboard Viva Wallet</p>
                          <div className="space-y-3">
                            <div>
                              <p className="text-xs text-blue-700 font-bold">1. WEBHOOK (Configuracoes {'>'} Acesso a API {'>'} Webhooks {'>'} Criar webhook)</p>
                              <p className="text-xs text-blue-600 mt-1">Tipo: <strong>Transaction Payment Created</strong></p>
                              <p className="text-xs text-blue-600">URL:</p>
                              <code className="text-xs bg-blue-100 p-2 rounded block break-all select-all font-bold">{`https://api.kaisosystem.com/api/public/viva-webhook`}</code>
                            </div>
                            <div>
                              <p className="text-xs text-blue-700 font-bold">2. URLs DE RETORNO (Vendas {'>'} Fontes de Pagamento {'>'} Editar fonte {vivaConfig.website_code || '7099'})</p>
                              <p className="text-xs text-blue-600">Success URL:</p>
                              <code className="text-xs bg-blue-100 p-2 rounded block break-all select-all">{`https://api.kaisosystem.com/api/public/viva-return`}</code>
                              <p className="text-xs text-blue-600 mt-1">Failure URL:</p>
                              <code className="text-xs bg-blue-100 p-2 rounded block break-all select-all">{`https://api.kaisosystem.com/api/public/viva-return`}</code>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {!activeGateway && (
            <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
              Ningun proveedor de pagos online esta activo. Seleccione uno para habilitar pagos con tarjeta.
            </p>
          )}
        </CardContent>
      </Card>

      {/* CONFIGURAÇÕES OPERACIONAIS */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Monitor className="w-5 h-5 text-emerald-600" />
            <CardTitle className="uppercase">Configurações Operacionais</CardTitle>
          </div>
          <CardDescription>Habilite ou desabilite módulos e configure o método de pagamento extra no POS.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Delivery toggle */}
          <div className="flex items-center justify-between py-2 border-b border-border/30">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-sm">Habilitar Delivery</p>
                <p className="text-xs text-muted-foreground">Permite receber pedidos de entrega ao domicílio</p>
              </div>
            </div>
            <Switch
              checked={deliveryEnabled}
              onCheckedChange={setDeliveryEnabled}
              data-testid="toggle-delivery-enabled"
            />
          </div>

          {/* POS toggle */}
          <div className="flex items-center justify-between py-2 border-b border-border/30">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Monitor className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-semibold text-sm">Habilitar POS</p>
                <p className="text-xs text-muted-foreground">Ativa o terminal de ponto de venda (caixa)</p>
              </div>
            </div>
            <Switch
              checked={posEnabled}
              onCheckedChange={setPosEnabled}
              data-testid="toggle-pos-enabled"
            />
          </div>

          {/* Extra payment method */}
          <div className="space-y-2">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-sm">Método de Pagamento Extra no POS</p>
                <p className="text-xs text-muted-foreground">Aparece no POS ao lado de Dinheiro e Cartão. Ex: MBWay, Bizum, Pix</p>
              </div>
            </div>
            <Input
              value={extraPaymentMethod}
              onChange={e => setExtraPaymentMethod(e.target.value)}
              placeholder="Ex: MBWay, Bizum, Pix, MB Net..."
              className="bg-secondary border-transparent max-w-sm"
              data-testid="extra-payment-method-input"
            />
            {extraPaymentMethod.trim() && (
              <p className="text-xs text-emerald-600">
                Vai aparecer no POS como botão: <strong>"{extraPaymentMethod.trim().toUpperCase()}"</strong>
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Button className="glow-orange" onClick={saveMethods} disabled={saving} data-testid="save-payment-methods-btn">
        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</> : <><Check className="w-4 h-4 mr-2" />ACTUALIZAR CONFIGURACION DE PAGOS</>}
      </Button>
    </div>
  );
};
